// Staff scanning page. Scanner-first, with a "Can't scan?" fallback that
// swaps in the Card-code / Search form (shared with the owner StampPage) for
// online / delivery orders where there is no physical pass to scan.
//
// On resolve — by QR decode or by code/email lookup — we navigate back to
// /staff with the stamp source in router state; the home page mounts
// StaffStampOverlay from there. Tearing down the scanner on resolve (instead
// of pausing it and floating an overlay above it) eliminates the
// double-trigger window where a still-visible customer QR could re-fire the
// flow even if the in-memory cooldown happened to bypass.

import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { ownerApi } from '../../owner/ownerApi.js'
import { RecoverPassModal } from '../../shared/recover-pass/RecoverPassModal.js'
import { StaffScanner, type ScannedToken } from '../components/StaffScanner.js'
import type { StaffStampSource } from '../components/StaffStampOverlay.js'
import type { StampCard } from '../../owner/components/StampConfirmOverlay.js'
import {
  StampFallbackForm,
  type StampFallbackMode,
} from '../../shared/stamp/StampFallbackForm.js'
import '../../shared/redemption/redemptionUI.css'
import '../../owner/pages/stampPage.css'
import './staff.css'

export function StaffScanPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [fallbackOpen, setFallbackOpen] = useState(false)
  const [fallbackTab, setFallbackTab] = useState<StampFallbackMode>('code')
  // True while we're pre-warming the destination overlay's card-detail query,
  // for every source — QR scan or code/search. Unlike the owner dashboard,
  // nothing here pre-warms the cache earlier, so this always fires; the
  // await (instead of a fire-and-forget prefetch) also guarantees the
  // overlay opens with warm cache rather than racing its own query against
  // the navigation.
  const [resolving, setResolving] = useState(false)
  // Staff pass recovery — offered on search results whose program has the
  // owner's "Staff can recover passes" checkbox on (server re-checks).
  const [recoverFor, setRecoverFor] = useState<StampCard | null>(null)

  const goStamp = useCallback(
    async (source: StaffStampSource) => {
      setResolving(true)
      try {
        await queryClient.fetchQuery({
          queryKey: ['staff', 'card-detail', source.cardId],
          queryFn: () =>
            ownerApi.get(`/cards/${encodeURIComponent(source.cardId)}`),
        })
      } catch {
        // Non-fatal — the overlay's own query will retry and surface the
        // error there instead of stranding staff on this page.
      }
      setResolving(false)
      navigate('/staff', { state: { stampSource: source } })
    },
    [navigate, queryClient],
  )

  const handleScanned = useCallback(
    (s: ScannedToken) => {
      void goStamp({ kind: 'scanned', cardId: s.cardId, rawToken: s.rawToken })
    },
    [goStamp],
  )

  const handleResolved = useCallback(
    (card: StampCard) => {
      void goStamp({ kind: 'lookup', cardId: card.card_id })
    },
    [goStamp],
  )

  return (
    <div className="stamp-page">
      <header className="stamp-header">
        <h1>Stamp</h1>
      </header>

      {fallbackOpen ? (
        <StampFallbackForm
          mode={fallbackTab}
          onModeChange={setFallbackTab}
          onResolved={handleResolved}
          onBack={() => setFallbackOpen(false)}
          searchPath="/staff/customers/search"
          onRecover={setRecoverFor}
        />
      ) : (
        <>
          <StaffScanner paused={false} onScanned={handleScanned} />
          <button
            type="button"
            className="stamp-fallback-link"
            onClick={() => setFallbackOpen(true)}
          >
            Can't scan? Type a code or search →
          </button>
        </>
      )}

      {recoverFor && (
        <RecoverPassModal
          cardId={recoverFor.card_id}
          customerName={recoverFor.first_name}
          onClose={() => setRecoverFor(null)}
        />
      )}

      {resolving && (
        <div className="stamp-page-resolving" aria-hidden="true">
          <span className="stamp-spinner-square stamp-spinner-square--lg" />
          <span className="stamp-scanner-captured-label">Opening card…</span>
        </div>
      )}
    </div>
  )
}
